/**
 * What the user has open in the editor, as an assistant reads it: the deck
 * page around the selection, the deck's Shared context, and the States
 * panel's context. Facts with their paths, so the model can name one as a
 * parent; where new content goes is the model's call.
 */

import type { SceneObject } from '../../core/SceneObject';
import { pageScopedContentRoots, isSharedIntent, resolvePageContext } from '../pages/pageContext';
import { findSharedGroup } from '../pages/sharedChildren';
import type { SceneToolHost } from './types';

const named = (object: SceneObject): string => `"${object.name}" (${object.getPath()})`;

/**
 * Lines naming what the user has open, or null when nothing is: no deck page
 * around the selection (none, or a deck with no page to name, such as a
 * `firstChild` deck) and no States context.
 */
export function describeEditorFocus(host: SceneToolHost): string | null {
    const lines: string[] = [];
    const selected = host.selected?.() ?? null;
    const deck = selected ? resolvePageContext(selected) : null;
    if (deck) {
        const { container, pages, pageCount } = deck;
        if (isSharedIntent(container)) {
            const group = findSharedGroup(container);
            lines.push(`Deck ${named(container)}: the user is editing its shared content, shown on every page${group ? `; its Shared group is ${named(group)}` : ''}.`);
        } else if (pages.isShowingAll()) {
            lines.push(`Deck ${named(container)}: showing all ${pageCount} pages at once.`);
        } else {
            const page = deck.selectedPage ?? pageScopedContentRoots(container)?.[0] ?? null;
            const index = page ? deck.selectedPage ? deck.selectedPageIndex : deck.activeIndex : -1;
            if (page) lines.push(`Deck ${named(container)}: page ${index + 1} of ${pageCount}, ${named(page)}, is open.`);
        }
    }
    const session = host.statesSession?.() ?? null;
    const context = session?.getContext() ?? null;
    if (session && context) {
        const state = session.active();
        lines.push(`States panel: context ${named(context)}, showing ${state === null ? 'Base' : `state "${state}"`}.`);
    }
    if (lines.length === 0) return null;
    return [
        'Open in the editor:',
        ...lines.map(line => `- ${line}`),
        'A request that names no place is usually about what is open: pass it as `parent` when the new object belongs there.',
    ].join('\n');
}
